import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "../lib/api";
import { ArrowRight, Calendar, Heart, BookOpen, Users } from "lucide-react";
import { SectionHeader } from "../components/Section";

const HERO_BG = "https://images.unsplash.com/photo-1438032005730-c779502df39b?crop=entropy&cs=srgb&fm=jpg&w=1800&q=85";

export default function Home() {
  const [sermons, setSermons] = useState([]);
  const [events, setEvents] = useState([]);
  const [announcements, setAnnouncements] = useState([]);

  useEffect(() => {
    api.get("/sermons").then((r) => setSermons(r.data.slice(0, 3))).catch(() => {});
    api.get("/events").then((r) => setEvents(r.data.slice(0, 3))).catch(() => {});
    api.get("/announcements").then((r) => setAnnouncements(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="Sanctuary" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-[#0a0a0a]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24 w-full">
          <div className="max-w-3xl">
            <div className="font-accent text-xs text-gold mb-6">EZEKIEL 48:35</div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-white leading-[1.05] tracking-tight">
              The name of the city from that day shall be, <span className="text-gold italic">Yahweh Shammah</span>.
            </h1>
            <p className="mt-8 text-lg text-zinc-300 leading-relaxed max-w-2xl">
              A gathered council walking in covenant with the Most High — keeping His commandments, honoring His Sabbaths, and testifying that Yahweh is truly there.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link data-testid="hero-visit-cta" to="/about" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-gold text-black font-semibold hover:bg-slate-200 transition-colors duration-200">
                Visit Us <ArrowRight className="w-4 h-4" />
              </Link>
              <Link data-testid="hero-sermons-cta" to="/sermons" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-white/20 text-white hover:border-gold hover:text-gold transition-colors duration-200">
                Watch Sermons
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="py-24 border-b hairline">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHeader eyebrow="OUR FOUNDATION" title="A house of prayer for all people" subtitle="Four pillars that define our assembly and shape everything we do." />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: BookOpen, t: "The Word", d: "The Scriptures are our authority, studied deeply and lived faithfully." },
              { icon: Heart, t: "Worship", d: "Reverent worship of the Most High every Sabbath and appointed feast." },
              { icon: Users, t: "Community", d: "A council of brothers and sisters bearing one another's burdens." },
              { icon: Calendar, t: "The Feasts", d: "We keep the appointed times as everlasting statutes." },
            ].map((p) => (
              <div key={p.t} className="border hairline rounded-xl p-7 surface hover:-translate-y-1 hover:border-gold/50 transition-transform duration-300">
                <p.icon className="w-6 h-6 text-gold mb-5" />
                <h3 className="font-display text-xl text-white mb-2">{p.t}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Latest Sermons */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div>
              <div className="font-accent text-xs text-gold mb-4">LATEST TEACHING</div>
              <h2 className="font-display text-4xl sm:text-5xl text-white">From the pulpit</h2>
            </div>
            <Link to="/sermons" className="text-sm text-gold hover:opacity-80 flex items-center gap-2">All sermons <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="home-sermons-grid">
            {sermons.map((s) => (
              <Link key={s.id} to="/sermons" className="group border hairline rounded-xl overflow-hidden surface hover:border-gold/50 transition-colors duration-300">
                <div className="aspect-video bg-black relative overflow-hidden">
                  {s.youtube_url ? (
                    <img src={`https://img.youtube.com/vi/${extractYouTubeId(s.youtube_url)}/hqdefault.jpg`} alt={s.title} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity duration-300" />
                  ) : <div className="w-full h-full bg-gradient-to-br from-zinc-800 to-black" />}
                </div>
                <div className="p-6">
                  <div className="font-accent text-[10px] text-gold mb-3">{s.date} · {s.scripture}</div>
                  <h3 className="font-display text-xl text-white group-hover:text-gold transition-colors duration-200">{s.title}</h3>
                  <p className="mt-2 text-sm text-zinc-400">{s.speaker}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Announcements + Events split */}
      <section className="py-24 border-t hairline surface">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-1">
            <div className="font-accent text-xs text-gold mb-4">ANNOUNCEMENTS</div>
            <h2 className="font-display text-4xl text-white mb-6">This week</h2>
            <div className="space-y-4">
              {announcements.map((a) => (
                <div key={a.id} className="border-l-2 border-gold pl-5 py-2">
                  <h4 className="font-display text-lg text-white">{a.title}</h4>
                  <p className="text-sm text-zinc-400 mt-1">{a.message}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-2">
            <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
              <div>
                <div className="font-accent text-xs text-gold mb-4">UPCOMING</div>
                <h2 className="font-display text-4xl text-white">Events & gatherings</h2>
              </div>
              <Link to="/events" className="text-sm text-gold flex items-center gap-2">All events <ArrowRight className="w-4 h-4" /></Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {events.map((e) => (
                <div key={e.id} className="border hairline rounded-xl overflow-hidden bg-black/40">
                  {e.image_url && <div className="aspect-[16/9] overflow-hidden"><img src={e.image_url} alt={e.title} className="w-full h-full object-cover" /></div>}
                  <div className="p-5">
                    <div className="font-accent text-[10px] text-gold mb-2">{new Date(e.date).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}</div>
                    <h4 className="font-display text-xl text-white">{e.title}</h4>
                    <p className="text-sm text-zinc-400 mt-1">{e.location}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Give CTA */}
      <section className="py-24">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center">
          <div className="font-accent text-xs text-gold mb-5">PARTNER WITH US</div>
          <h2 className="font-display text-4xl sm:text-5xl text-white mb-6">Every gift builds the assembly</h2>
          <p className="text-zinc-400 max-w-2xl mx-auto leading-relaxed">Your generosity supports the ministry, the prayer centers, and the outreach of the Council. Give with a cheerful heart.</p>
          <Link data-testid="home-give-cta" to="/give" className="mt-9 inline-flex items-center gap-2 px-8 py-4 rounded-full bg-gold text-black font-semibold hover:bg-slate-200 transition-colors duration-200">
            Give an Offering <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}

function extractYouTubeId(url) {
  const m = url.match(/(?:youtube\.com\/(?:embed\/|watch\?v=)|youtu\.be\/)([\w-]{11})/);
  return m ? m[1] : "";
}
